import React, { useEffect, useMemo, useState } from "react"
import { BusinessHours, HorairesOuvertureDepartmentEnum, WeekDayEnum, WorkingHour } from "./type"
import ModalWorkingHoursForm from "./ModalWorkingHoursForm"
import Select from 'react-select'
import { SelectOption } from "../../types/SelectOption"
import BackOfficeAPIClient from "../../clients/BackofficeAPIClient"

declare const window: any

type ModalMode = "create" | "edit" | "duplicate"

type Props = {
    workingHours: WorkingHour[] | null
    refetchData: () => void
    mode?: ModalMode
    allWorkingHours?: WorkingHour[]
    dealerships?: SelectOption[]
    isGlobalConfig?: boolean
}

export function ModalWorkingHours(props: Props) {
    const backofficeApiClient = new BackOfficeAPIClient()
    /**
     * Récupère le nom du département à partir de sa valeur numérique
     * @param department - La valeur du département (1 ou 2)
     * @returns Le nom du département (APV ou Commerce)
     */
    const getDepartmentLabel = (department: HorairesOuvertureDepartmentEnum): string => {
        return Object.entries(HorairesOuvertureDepartmentEnum)
            .find(([key, value]) => value === department)?.[0] || String(department)
    }

    const initFormObject = (): WorkingHour[] => {
        return Object.values(HorairesOuvertureDepartmentEnum)
            .filter((value) => typeof value === 'number')
            .map((value) => {
                return {
                    dealershipIds: null,
                    department: value as HorairesOuvertureDepartmentEnum,
                    schedules: {
                        businessHours: {} as BusinessHours,
                    },
                } as WorkingHour
            })
    }

    const [ ongletActif, setOngletActif ] = useState<HorairesOuvertureDepartmentEnum | null>(null)
    const [ selectedDepartement, setSelectedDepartement ] = useState<HorairesOuvertureDepartmentEnum[]>([])
    const [ workingHours, setWorkingHours ] = useState<WorkingHour[]>()
    const [ mode, setMode ] = useState<ModalMode>("create")

    /**
     * Réinitialise le formulaire à son état initial
     */
    const resetForm = (): void => {
        setSelectedDepartement([])
        setWorkingHours(undefined)
        setOngletActif(null)
    }

    useEffect(() => {
        if (props.workingHours && props.workingHours.length > 0) {
            const departments = props.workingHours.map(wh => wh.department)
            setSelectedDepartement(departments)
        }
    }, [ props.workingHours ])

    useEffect(() => {
        if (props.mode) {
            setMode(props.mode)
        } else if (props.workingHours && props.workingHours.length > 0) {
            setMode("edit")
        } else {
            setMode("create")
        }
    }, [ props.mode, props.workingHours ])

    const modalTitle = useMemo(() => {
        if (mode === "edit") return "Modification horaires d'ouvertures"
        if (mode === "duplicate") return "Dupliquer horaires d'ouverture"
        return "Horaires d'ouvertures"
    }, [ mode ])

    const optionsDepartement = useMemo(() => {
        const allOptions = Object.entries(HorairesOuvertureDepartmentEnum)
            .filter(([key, value]) => typeof value === 'number')
            .map(([key, value]) => {
                return { label: key, value: value }
            })

        // En mode création de config globale, filtrer les départements déjà utilisés en global
        if (mode === "create" && props.isGlobalConfig && props.allWorkingHours) {
            return allOptions.filter(opt => {
                // Vérifier si ce département a déjà une config globale
                const hasGlobalConfig = props.allWorkingHours?.some(wh =>
                    wh.department === opt.value &&
                    (!wh.dealershipIds || wh.dealershipIds.length === 0)
                )
                return !hasGlobalConfig
            })
        }

        return allOptions
    }, [mode, props.isGlobalConfig, props.allWorkingHours])

    const selectedDepartementOptions = useMemo(() => {
        return optionsDepartement.filter(opt => selectedDepartement.includes(opt.value as HorairesOuvertureDepartmentEnum))
    }, [ selectedDepartement ])

    useEffect(() => {
        if (selectedDepartement.length === 0) {
            setOngletActif(null)
        } else if (selectedDepartement.length === 1) {
            setOngletActif(selectedDepartement[0])
        } else if (selectedDepartement.length > 1) {
            if (ongletActif === null || !selectedDepartement.includes(ongletActif)) {
                setOngletActif(selectedDepartement[0])
            }
        }
    }, [ selectedDepartement, ongletActif ])

    useEffect(() => {
        setWorkingHours(prevWorkingHours => {
            return selectedDepartement.map(dept => {
                const existing = prevWorkingHours?.find(wh => wh.department === dept)
                if (existing) {
                    return existing
                }
                return {
                    dealershipIds: null,
                    department: dept,
                    schedules: {
                        businessHours: {} as BusinessHours,
                    },
                } as WorkingHour
            })
        })
    }, [ selectedDepartement ])

    useEffect(() => {
        if (props.workingHours && props.workingHours.length > 0) {
            if (mode === "duplicate") {
                // En mode duplication, on copie les données sans l'ID et on réinitialise les sites
                const duplicatedWorkingHours = props.workingHours.map(wh => ({
                    ...wh,
                    id: undefined,
                    dealershipIds: null,
                }))
                setWorkingHours(duplicatedWorkingHours)
            } else {
                // Mode edit
                setWorkingHours(props.workingHours)
            }
        } else {
            // Mode create
            setSelectedDepartement([])
            setWorkingHours(initFormObject())
        }
    }, [ props.workingHours, mode ])

    const handleWorkingHourChange = (department: HorairesOuvertureDepartmentEnum, updatedWorkingHour: WorkingHour): void => {
        setWorkingHours(prevWorkingHours =>
            prevWorkingHours?.map(wh =>
                wh.department === department
                    ? updatedWorkingHour
                    : wh,
            ),
        )
    }

    const addSpecificPeriod = (department: HorairesOuvertureDepartmentEnum): void => {
        setWorkingHours(prevWorkingHours =>
            prevWorkingHours?.map(wh =>
                wh.department === department
                    ? {
                        ...wh,
                        schedules: {
                            ...wh.schedules,
                            specificPeriod: [
                                ...(wh.schedules.specificPeriod || []),
                                { label: '', startDate: '', endDate: '', businessHours: {} as BusinessHours },
                            ],
                        },
                    }
                    : wh,
            ),
        )
    }

    const removeSpecificPeriod = (department: HorairesOuvertureDepartmentEnum, index: number): void => {
        setWorkingHours(prevWorkingHours =>
            prevWorkingHours?.map(wh =>
                wh.department === department
                    ? {
                        ...wh,
                        schedules: {
                            ...wh.schedules,
                            specificPeriod: wh.schedules.specificPeriod?.filter((_, i) => i !== index),
                        },
                    }
                    : wh,
            ),
        )
    }

    const handleSpecificPeriodChange = (
        department: HorairesOuvertureDepartmentEnum,
        index: number,
        field: 'label' | 'startDate' | 'endDate',
        value: string,
    ): void => {
        setWorkingHours(prevWorkingHours =>
            prevWorkingHours?.map(wh =>
                wh.department === department && wh.schedules.specificPeriod
                    ? {
                        ...wh,
                        schedules: {
                            ...wh.schedules,
                            specificPeriod: wh.schedules.specificPeriod.map((period, i) =>
                                i === index
                                    ? { ...period, [field]: value }
                                    : period,
                            ),
                        },
                    }
                    : wh,
            ),
        )
    }

    const handlespecificPeriodChange = (
        department: HorairesOuvertureDepartmentEnum,
        index: number,
        updatedWorkingHour: WorkingHour,
    ): void => {
        setWorkingHours(prevWorkingHours =>
            prevWorkingHours?.map(wh =>
                wh.department === department && wh.schedules.specificPeriod
                    ? {
                        ...wh,
                        schedules: {
                            ...wh.schedules,
                            specificPeriod: wh.schedules.specificPeriod.map((period, i) =>
                                i === index
                                    ? { ...period, businessHours: updatedWorkingHour.schedules.businessHours }
                                    : period,
                            ),
                        },
                    }
                    : wh,
            ),
        )
    }

    /**
     * Vérifie que tous les créneaux horaires sont complets (pas d'heure manquante)
     */
    const validateTimeSlots = (): boolean => {
        if (!workingHours) return false

        for (const wh of workingHours) {
            // Vérifier les horaires standards (businessHours)
            const businessHours = wh.schedules.businessHours
            for (const day in businessHours) {
                const slots = businessHours[day as WeekDayEnum]
                if (slots) {
                    for (const slot of slots) {
                        if (!slot[0] || !slot[1]) {
                            return false
                        }
                    }
                }
            }

            // Vérifier les périodes spécifiques
            if (wh.schedules.specificPeriod) {
                for (const period of wh.schedules.specificPeriod) {
                    // Vérifier que les dates sont renseignées
                    if (!period.startDate || !period.endDate || !period.label) {
                        return false
                    }

                    // Vérifier les créneaux de la période spécifique
                    for (const day in period.businessHours) {
                        const slots = period.businessHours[day as WeekDayEnum]
                        if (slots) {
                            for (const slot of slots) {
                                if (!slot[0] || !slot[1]) {
                                    return false
                                }
                            }
                        }
                    }
                }
            }
        }

        return true
    }

    /**
     * Valide le formulaire avant la sauvegarde
     * @returns true si valide, false sinon
     */
    const validateForm = (): boolean => {
        // Vérifier que tous les créneaux horaires sont complets
        if (!validateTimeSlots()) {
            alert("Veuillez remplir tous les créneaux horaires (heure de début et de fin)")
            return false
        }

        // En mode duplication, on doit avoir au moins un site sélectionné
        if (mode === "duplicate") {
            const hasNoSites = workingHours?.some(wh => !wh.dealershipIds || wh.dealershipIds.length === 0)
            if (hasNoSites) {
                alert("Veuillez sélectionner au moins un site pour la duplication")
                return false
            }
        }

        return true
    }

    /**
     * Sauvegarde les horaires d'ouverture (création ou modification)
     */
    const handleSave = async (): Promise<void> => {
        if (!validateForm()) return

        try {
            if (mode === "edit") {
                await backofficeApiClient.put('/workingHours', { workingHours })
            } else {
                await backofficeApiClient.post('/workingHours', { workingHours })
            }

            props.refetchData()
            window.$('#modalWorkingHours').modal('hide')
        } catch (e) {
            console.error(`ERREUR lors de la sauvegarde des horaires d'ouverture`, e)
        }
    }

    return (
        <div id="modalWorkingHours" className="modal fade" tabIndex={-1} role="dialog" data-backdrop="static" data-keyboard="false">
            <div className="modal-dialog modal-dialog-centered modal-xl">
                <div className="modal-content border-0">
                    <div className="modal-header p-2 bg-light">
                        <span className="tw-text-xl tw-font-semibold">{modalTitle}</span>
                    </div>

                    <div className="modal-body">
                        <div className="tw-flex tw-flex-col tw-gap-3">
                            {mode === "create" && <div className="tw-flex tw-flex-row tw-items-center tw-gap-3 tw-text-sm">
                              <span className="tw-w-32">Département(s) :</span>
                              <Select
                                multi={true}
                                style={{ minWidth: '15em' }}
                                value={selectedDepartementOptions}
                                onChange={(options: SelectOption[] | null) => {
                                    if (options && options.length > 0) {
                                        setSelectedDepartement(options.map((opt) => opt.value as HorairesOuvertureDepartmentEnum))
                                    } else {
                                        setSelectedDepartement([])
                                    }
                                }}
                                options={optionsDepartement}
                              />
                            </div>}
                            {selectedDepartement.length > 0 && (
                                <ul className="nav nav-tabs" id="departementWorkingHours">
                                    {selectedDepartement.map((department) => {
                                        const deptLabel = getDepartmentLabel(department)
                                        return (
                                            <li key={department} className="nav-item">
                                                <a
                                                    className={`nav-link ${ongletActif === department ? 'active' : ''}`}
                                                    style={{ fontSize: '.9em' }}
                                                    id={`${deptLabel.toLowerCase()}-tab`}
                                                    data-toggle="tab"
                                                    href={`#dept-${department}`}
                                                    role="tab"
                                                    aria-controls={deptLabel.toLowerCase()}
                                                    aria-selected={ongletActif === department}
                                                    onClick={() => setOngletActif(department)}
                                                >
                                                    {deptLabel}
                                                </a>
                                            </li>
                                        )
                                    })}
                                </ul>
                            )}
                            <div className="tab-content" id="myTabContent" style={{ paddingTop: '15px' }}>
                                {selectedDepartement.map((department) => {
                                    const deptLabel = getDepartmentLabel(department)
                                    return (
                                        <div
                                            key={department}
                                            className={`tab-pane tw-pb-4 ${ongletActif === department ? 'show active' : 'fade'}`}
                                            id={`dept-${department}`}
                                            role="tabpanel"
                                            aria-labelledby={`${deptLabel.toLowerCase()}-tab`}
                                        >
                                        <div className="tw-flex tw-flex-col tw-gap-3">
                                            <ModalWorkingHoursForm
                                                isEditing={mode === "edit"}
                                                workingHours={workingHours?.find((wh) => wh.department === department)}
                                                onChange={(updatedWorkingHour) => handleWorkingHourChange(department, updatedWorkingHour)}
                                                allWorkingHours={props.allWorkingHours}
                                                dealerships={props.dealerships}
                                                hideDealershipSelect={mode === "create" && props.isGlobalConfig}
                                            />

                                            <div className="tw-flex tw-flex-row tw-items-center tw-gap-2">
                                                <button
                                                    type="button"
                                                    className="tw-text-sm tw-rounded tw-bg-blue-500 tw-text-white tw-border-0 tw-px-3 tw-py-2"
                                                    onClick={() => addSpecificPeriod(department)}
                                                >
                                                    <i className="fa fa-plus tw-mr-2"></i>
                                                    Ajouter une période spécifique
                                                </button>
                                            </div>

                                            {workingHours?.find((wh) => wh.department === department)?.schedules.specificPeriod?.map((period, index) => (
                                                <div key={index} className="tw-flex tw-flex-col tw-gap-3 tw-mt-3 tw-p-4 tw-border tw-rounded tw-bg-gray-50">
                                                    <div className="tw-flex tw-flex-row tw-justify-between tw-items-center">
                                                        <span className="tw-font-semibold tw-text-lg">Période spécifique #{index + 1}</span>
                                                        <button
                                                            type="button"
                                                            className="tw-text-red-500 tw-border-0 tw-bg-transparent"
                                                            onClick={() => removeSpecificPeriod(department, index)}
                                                            title="Supprimer cette période"
                                                        >
                                                            <i className="fa fa-trash"></i>
                                                        </button>
                                                    </div>

                                                    <div className="tw-flex tw-flex-row tw-items-center tw-gap-3 tw-text-sm">
                                                        <span className="tw-w-28">Label :</span>
                                                        <input
                                                            type="text"
                                                            className="tw-border tw-border-gray-300 tw-rounded tw-px-3 tw-py-2 tw-flex-1"
                                                            placeholder="Ex: Période de Noël, Été, etc."
                                                            value={period.label}
                                                            onChange={(e) => handleSpecificPeriodChange(department, index, 'label', e.target.value)}
                                                        />
                                                    </div>

                                                    <div className="tw-flex tw-flex-row tw-gap-5">
                                                        <div className="tw-flex tw-flex-row tw-items-center tw-gap-3 tw-text-sm">
                                                            <span className="tw-w-28">Date de début :</span>
                                                            <input
                                                                type="date"
                                                                className="tw-border tw-border-gray-300 tw-rounded tw-px-3 tw-py-2"
                                                                value={period.startDate}
                                                                onChange={(e) => handleSpecificPeriodChange(department, index, 'startDate', e.target.value)}
                                                            />
                                                        </div>

                                                        <div className="tw-flex tw-flex-row tw-items-center tw-gap-4 tw-text-sm">
                                                            <span className="">Date de fin :</span>
                                                            <input
                                                                type="date"
                                                                className="tw-border tw-border-gray-300 tw-rounded tw-px-3 tw-py-2"
                                                                value={period.endDate}
                                                                onChange={(e) => handleSpecificPeriodChange(department, index, 'endDate', e.target.value)}
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="tw-flex tw-flex-col tw-gap-3">
                                                        <span className="tw-font-medium tw-text-sm">Horaires de la période spécifique :</span>
                                                        <ModalWorkingHoursForm
                                                            isSpecificPeriod
                                                            isEditing={mode === "edit"}
                                                            workingHours={{
                                                                dealershipIds: null,
                                                                department: department,
                                                                schedules: {
                                                                    businessHours: period.businessHours || {},
                                                                },
                                                            }}
                                                            onChange={(updatedWorkingHour) => handlespecificPeriodChange(department, index, updatedWorkingHour)}
                                                        />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    )
                                })}
                            </div>
                        </div>

                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" data-dismiss="modal" onClick={resetForm}>Fermer</button>
                            <button
                                type="button"
                                onClick={handleSave}
                                className={mode === "duplicate" ? "btn btn-success" : "btn btn-primary"}
                                disabled={!workingHours || workingHours.length === 0}
                            >
                                {mode === "duplicate" ? "Dupliquer" : "Enregistrer"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
